import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Calculator, CheckCheck } from "lucide-react";
import Aurora from "@/components/Aurora";
import { MaskedLines } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const CHAT = [
  { from: "client", text: "Oi! Vocês têm horário amanhã de manhã?" },
  { from: "ia", text: "Oi, Ana! Temos sim — 9h30 ou 10h45. Qual prefere?" },
  { from: "client", text: "10h45, por favor" },
  { from: "ia", text: "Perfeito. Agendado para amanhã às 10h45. Te envio um lembrete na véspera." },
];
const DELAYS = [1800, 2400, 1600, 2600, 4200];

const PhoneMockup = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setTimeout(
      () => setStep((s) => (s >= CHAT.length ? 0 : s + 1)),
      DELAYS[Math.min(step, DELAYS.length - 1)]
    );
    return () => clearTimeout(id);
  }, [step]);

  const typing = step < CHAT.length && CHAT[step].from === "ia";

  return (
    <div
      data-testid="hero-whatsapp-mockup"
      className="glass-dark float-slow w-[320px] rounded-[28px] p-4 shadow-[0_40px_80px_-20px_rgba(4,20,28,0.6)] sm:w-[360px]"
    >
      <div className="mb-3 flex items-center gap-3 border-b border-white/10 pb-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-turquoise font-display text-base font-bold text-petrol">
          C
        </div>
        <div>
          <p className="font-display text-sm font-semibold text-white">CliniAtiva IA</p>
          <p className="flex items-center gap-1.5 text-xs text-sage">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> online agora
          </p>
        </div>
        <span className="ml-auto rounded-full border border-turquoise/40 px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-widest text-turquoise">
          24/7
        </span>
      </div>

      <div className="flex min-h-[240px] flex-col gap-2.5">
        <AnimatePresence>
          {CHAT.slice(0, step).map((m, i) => (
            <motion.div
              key={`${i}-${m.text.slice(0, 6)}`}
              initial={{ opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-snug ${
                m.from === "ia"
                  ? "self-start rounded-bl-sm bg-turquoise text-petrol font-medium"
                  : "self-end rounded-br-sm bg-white/10 text-white"
              }`}
            >
              {m.text}
              {m.from === "ia" && i === CHAT.length - 1 && (
                <CheckCheck className="ml-1.5 inline h-3.5 w-3.5 text-petrol/70" />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {typing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-1 self-start rounded-2xl rounded-bl-sm bg-turquoise/25 px-4 py-3"
          >
            {[0, 1, 2].map((d) => (
              <motion.span
                key={d}
                className="h-1.5 w-1.5 rounded-full bg-turquoise"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 0.9, repeat: Infinity, delay: d * 0.2 }}
              />
            ))}
          </motion.div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-full bg-white/5 px-4 py-2.5">
        <span className="text-xs text-white/40">Mensagem respondida pela IA…</span>
        <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-turquoise">
          <ArrowRight className="h-3 w-3 text-petrol" />
        </span>
      </div>
    </div>
  );
};

const STATS = [
  { value: "3s*", label: "resposta média estimada" },
  { value: "24/7", label: "operação contínua" },
  { value: "0*", label: "mensagens sem resposta" },
];

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const px = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const py = useTransform(sy, [-0.5, 0.5], [-10, 10]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="grain relative flex min-h-screen items-center overflow-hidden bg-petrol pt-[72px]"
    >
      <Aurora className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,transparent_35%,rgba(8,24,32,0.55)_100%)]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-5 py-20 lg:grid-cols-[1.15fr_0.85fr] lg:px-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mb-8 flex flex-wrap items-center gap-3"
          >
            <span className="flex items-center gap-2 rounded-full border border-turquoise/30 bg-turquoise/10 px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-turquoise sm:text-[11px]">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full animate-ping rounded-full bg-turquoise opacity-70" />
                <span className="h-2 w-2 rounded-full bg-turquoise" />
              </span>
              IA em operação agora
            </span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-sage/70 sm:text-[11px]">
              Tecnologia para negócios de atendimento
            </span>
          </motion.div>

          <MaskedLines
            as="h1"
            inView={false}
            delay={0.45}
            lines={[
              "Pare de perder",
              "clientes por demora",
              <>no <span className="italic text-turquoise">atendimento.</span></>,
            ]}
            className="font-display text-[42px] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-sage/90 sm:text-lg"
          >
            A CliniAtiva coloca agentes de IA no seu WhatsApp para responder em
            segundos, agendar, confirmar e recuperar clientes.
            <span className="mt-2 block font-semibold text-white">
              Sua equipe atende pessoas. A CliniAtiva cuida do resto.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("demonstracao")}
              className="group flex items-center gap-3 rounded-full bg-turquoise px-7 py-4 font-display text-base font-bold text-petrol transition-[transform,box-shadow] duration-300 hover:scale-[1.04] hover:shadow-[0_16px_40px_rgba(39,179,176,0.45)]"
            >
              Agendar demonstração gratuita
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-calculator"
              onClick={() => scrollToId("calculadora")}
              className="group flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-display text-base font-semibold text-white transition-[border-color,background-color] duration-300 hover:border-turquoise hover:bg-turquoise/10"
            >
              <Calculator className="h-5 w-5 text-turquoise" />
              Calcular minha economia
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.split(" ")[0]}`}>
                <p className="font-display text-2xl font-extrabold text-turquoise sm:text-3xl">{s.value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-sage/70 sm:text-xs">{s.label}</p>
              </div>
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-3 max-w-lg text-[11px] text-sage/40"
          >
            * Estimativas ilustrativas, sem dados auditados.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 2 }}
          transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ x: px, y: py }}
          className="mx-auto hidden will-change-transform transform-gpu md:block"
        >
          <PhoneMockup />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="flex h-10 w-6 items-start justify-center rounded-full border border-white/25 p-1.5"
        >
          <div className="h-2 w-1 rounded-full bg-turquoise" />
        </motion.div>
      </motion.div>
    </section>
  );
}
